import { Link } from 'react-router-dom'

import { ARTIFACT_ACTION, ARTIFACT_CARD_LINK } from './styles'

/**
 * A card's one action, as the in-app link it is (SH-4 of the 2026-10-15 web
 * design review: it was a 9px label with no padding and no press state, about
 * 11px tall, and it is the home screen's main action).
 *
 * Two shapes, by what the card holds:
 *
 * - **`stretch`**: the card is the target. The link takes no box of its own;
 *   its `::after` is pulled over the whole card, so the card must be
 *   `position: relative` (`ArtifactCard` makes it so when it has an action).
 *   Nothing inside the card is wrapped in the `<a>`, so a card can hold other
 *   content without nesting anything interactive in a link. Shipping fitness
 *   apps make the whole "next workout" card the target and not a small link
 *   inside it. What the card does under a pointer, a press and the keyboard is
 *   `ARTIFACT_CARD_LINK`.
 * - **default**: the card holds a control of its own (the video card's play
 *   button is the card's main action), so the link grows a box of its own:
 *   a `::before` pulled out 8px sideways and 16px up and down, 44px tall around
 *   the label, and nothing around it moves. The press dims the label. It sits
 *   at the end of its row (`ml-auto`) so that it can wrap under a long title.
 *
 * Both are `Link`, never `<a>`: an artifact href is always an app route, and a
 * full page load would throw away the streamed conversation.
 */
export function ArtifactAction({
  href,
  label,
  stretch = false,
  onFollow,
}: {
  href: string
  label: string
  stretch?: boolean
  /** Called when the link is followed (a press, or Enter on it), before the navigation. */
  onFollow?: () => void
}) {
  return (
    <Link
      to={href}
      onClick={onFollow}
      style={ARTIFACT_ACTION}
      className={
        stretch
          ? ARTIFACT_CARD_LINK
          : "relative ml-auto transition-opacity duration-150 ease-out before:absolute before:-inset-x-2 before:-inset-y-4 before:content-[''] active:opacity-55"
      }
    >
      {label}
    </Link>
  )
}
